import { TypeKeys } from '../actions/index.js';
import { updateActionStatePhase } from '../common/index.js';
import { ActionPhase } from '../common/storeUtils.js';
import { mergeArrays, reduceCrudAction } from './reducerUtils.js';
import type { SubscriptionActionsState } from '../../common/types/states.js';
import type { SubscriptionAction } from '../../generated/api/models.js';
import type { ActionTypes } from '../actions/index.js';
import type { ActionsHistory } from '../common/store.js';

export function pendingSubscriptionActionsReducer(
  originalState: (SubscriptionActionsState & ActionsHistory) | null,
  action: ActionTypes
): (SubscriptionActionsState & ActionsHistory) | null {
  if (typeof originalState === 'undefined') {
    return null;
  }

  // Backup because not having AUTH/LOGIN stuff here anymore can cause the app to crash when state is null and spread operator is used
  const state = originalState || ({} as SubscriptionActionsState & ActionsHistory);

  switch (action.type) {
    case TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CREATE_CONTACT_FOR_SUBSCRIPTION_USER_INFO,
          state,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.POLL_FOR_MYSELF_CHANGES_FULFILLED: {
      if (action.subscriptionAction) {
        return {
          ...state,
          actions: updateActionStatePhase(
            TypeKeys.POLL_FOR_MYSELF_CHANGES,
            state!,
            ActionPhase.IN_PROGRESS,
            ActionPhase.SUCCESS
          ),
          items: mergeArrays<SubscriptionAction>(
            'subscriptionActionId',
            'lastModified',
            [action.subscriptionAction],
            state!.items
          ),
          saving: false,
        };
      }
      return state;
    }

    case TypeKeys.UPDATE_PENDING_SUBSCRIPTION_ACTIONS: {
      return {
        ...state,
        items: mergeArrays<SubscriptionAction>(
          'subscriptionActionId',
          'lastModified',
          state?.items ? state.items : undefined,
          action.pendingSubscriptionActions
        ),
        saving: false,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO: {
      return {
        ...reduceCrudAction(action, state),
        saving: true,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        editingSection: undefined,
        errors: undefined,
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
        duplicateContactFound: false,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO_WITH_DUPLICATE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CHANGE_SUBSCRIPTION_USER_INFO,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
        duplicateContactFound: true,
      };
    }

    case TypeKeys.EDIT_SUBSCRIPTION_SECTION: {
      return {
        ...state,
        editingSection: action.editingSection,
        saving: false,
        duplicateContactFound: false,
      };
    }

    case TypeKeys.TERMINATE_SUBSCRIPTION: {
      return {
        ...reduceCrudAction(action, state),
        saving: false,
        terminationInProgress: true,
      };
    }

    case TypeKeys.TERMINATE_SUBSCRIPTION_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.TERMINATE_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
        terminateSubscriptionInitiated: false,
        terminationInProgress: false,
      };
    }

    case TypeKeys.TERMINATE_SUBSCRIPTION_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.TERMINATE_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
        terminationInProgress: false,
      };
    }

    case TypeKeys.SHOW_TERMINATE_SUBSCRIPTION_FORM: {
      return {
        ...state,
        saving: false,
        terminateSubscriptionInitiated: true,
      };
    }

    case TypeKeys.CLOSE_TERMINATE_SUBSCRIPTION_FORM: {
      return {
        ...state,
        saving: false,
        terminateSubscriptionInitiated: false,
      };
    }

    case TypeKeys.SHOW_SUSPEND_SUBSCRIPTION_OPTIONS: {
      return {
        ...state,
        saving: false,
        suspendSubscriptionInitiated: true,
      };
    }

    case TypeKeys.CLOSE_SUSPEND_SUBSCRIPTION_OPTIONS: {
      return {
        ...state,
        saving: false,
        suspendSubscriptionInitiated: false,
      };
    }

    case TypeKeys.SUSPEND_SUBSCRIPTION: {
      return {
        ...reduceCrudAction(action, state),
        saving: false,
        suspendSubscriptionInitiated: false,
      };
    }

    case TypeKeys.SUSPEND_SUBSCRIPTION_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.SUSPEND_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
      };
    }

    case TypeKeys.SUSPEND_SUBSCRIPTION_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.SUSPEND_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        saving: false,
      };
    }

    case TypeKeys.RESUME_SUBSCRIPTION: {
      return {
        ...reduceCrudAction(action, state),
        saving: false,
      };
    }

    case TypeKeys.RESUME_SUBSCRIPTION_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESUME_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
      };
    }

    case TypeKeys.RESUME_SUBSCRIPTION_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.RESUME_SUBSCRIPTION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION: {
      const errors = action.validationFn(action.pbxConfiguration);
      if (errors) {
        return {
          ...state,
          errors: errors,
          saving: false,
        };
      }
      return {
        ...reduceCrudAction(action, state),
        errors: undefined,
        saving: true,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        editingSection: undefined,
        errors: undefined,
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
      };
    }

    case TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.CHANGE_SUBSCRIPTION_PBX_CONFIGURATION,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.CHANGE_ADDONS: {
      return {
        ...reduceCrudAction(action, state),
        saving: true,
      };
    }

    case TypeKeys.CHANGE_ADDONS_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.CHANGE_ADDONS, state!, ActionPhase.IN_PROGRESS, ActionPhase.FAILED),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.CHANGE_ADDONS_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(TypeKeys.CHANGE_ADDONS, state!, ActionPhase.IN_PROGRESS, ActionPhase.SUCCESS),
        editingSection: undefined,
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state!.items, [
          action.subscriptionAction,
        ]),
        saving: false,
      };
    }

    case TypeKeys.CLEAR_STATE: {
      return null;
    }

    case TypeKeys.POST_EPP_REDEEM_TERMINATE_FULFILLED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.POST_EPP_REDEEM_TERMINATE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.SUCCESS
        ),
        items: mergeArrays<SubscriptionAction>('subscriptionActionId', 'lastModified', state?.items || [], [
          action.response,
        ]),
        saving: false,
      };
    }

    case TypeKeys.POST_EPP_REDEEM_TERMINATE_FAILED: {
      return {
        ...state,
        actions: updateActionStatePhase(
          TypeKeys.POST_EPP_REDEEM_TERMINATE,
          state!,
          ActionPhase.IN_PROGRESS,
          ActionPhase.FAILED
        ),
        errors: action.errors,
        saving: false,
      };
    }

    case TypeKeys.USE_DUPLICATE_CONTACT: {
      return {
        saving: false,
        editingSection: 'purposeOfUse',
      };
    }

    default:
      return state;
  }
}
